import {
  Badge,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Spinner,
  Wrap,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Branch } from '@texas/api/endpoints/branchesApi';
import { SupplierComplianceDocument } from '@texas/api/endpoints/supplierComplianceDocumentsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { useSupplierComplianceDocumentBranches } from './useSupplierComplianceDocumentBranches';

interface Props {
  document: SupplierComplianceDocument;
  branches: Branch[];
}

export function SupplierComplianceDocumentBranches({
  document,
  branches,
}: Props) {
  const { t } = useTranslation();
  const {
    apply,
    close,
    error,
    hasChanges,
    isOpen,
    loading,
    open,
    selectedBranchIds,
    selectBranches,
  } = useSupplierComplianceDocumentBranches(document);

  const documentName = document.label ?? document.file.originalName;

  return (
    <Wrap shouldWrapChildren={true}>
      {document.branches.length > 0 ? (
        document.branches.map((branch) => (
          <Badge
            alignItems="center"
            display="inline-flex"
            h="32px"
            key={branch.id}
            title={`${branch.name} (${branch.identifier})`}
            variant="configuration-reference-selected"
          >
            {branch.name} · {branch.identifier}
          </Badge>
        ))
      ) : (
        <Badge
          alignItems="center"
          display="inline-flex"
          h="32px"
          variant="configuration-reference"
        >
          {t('suppliers.workspace.global')}
        </Badge>
      )}
      <RoleRestrict
        allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
      >
        <Menu
          closeOnSelect={false}
          isLazy={true}
          isOpen={isOpen}
          placement="bottom-start"
          variant="entityDirectory"
          onClose={close}
          onOpen={open}
        >
          {document.branches.length > 0 ? (
            <MenuButton
              as={IconButton}
              aria-label={t('suppliers.workspace.editBranchAssignments', {
                name: documentName,
              })}
              icon={<Icons.Plus />}
              isDisabled={branches.length === 0}
              size="sm"
              variant="entityDirectorySecondary"
            />
          ) : (
            <MenuButton
              as={Button}
              leftIcon={<Icons.Plus />}
              isDisabled={branches.length === 0}
              size="sm"
              variant="entityDirectorySecondary"
            >
              {t('suppliers.workspace.assign')}
            </MenuButton>
          )}
          <Portal>
            <MenuList>
              <ErrorDetails error={error} />
              <MenuOptionGroup
                title={t('suppliers.workspace.branchSelectionSummary', {
                  name: documentName,
                  count: selectedBranchIds.length,
                })}
                type="checkbox"
                value={selectedBranchIds.map(String)}
                onChange={selectBranches}
              >
                {branches.map((branch) => (
                  <MenuItemOption
                    key={branch.id}
                    isDisabled={loading}
                    value={branch.id.toString()}
                  >
                    {branch.name} ({branch.identifier})
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuItem isDisabled={loading} onClick={close}>
                {t('general.cancel')}
              </MenuItem>
              <MenuItem
                icon={loading ? <Spinner size="xs" /> : <Icons.Checkmark />}
                isDisabled={loading || !hasChanges}
                onClick={apply}
              >
                {t('suppliers.workspace.applyBranches')}
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </RoleRestrict>
    </Wrap>
  );
}
